/**
 * The chart palette, shared by the metric view screen's full-size chart, the metric
 * tooltip and the BlinkPages site analytics report so the same quantity is inked the
 * same way wherever it is drawn.
 *
 * Plain strings only — this module must never import echarts. The metric tooltip
 * imports it statically and the scorecard drawer imports that (both are reachable
 * from app.js, the single Vite entry on every layout), so a charting import here
 * would ship the whole echarts payload on the login screen and undo the charts'
 * lazy loading.
 *
 * The TOOLTIP_LEGEND swatches in ./metric-tooltip deliberately spell these hexes
 * literally rather than interpolating BRAND_HEX: they are Tailwind arbitrary-value
 * classes, and Tailwind's scanner only emits classes it can see as literal strings
 * in the source — `border-[${BRAND_HEX}]` would compile to nothing and leave the
 * swatch unstyled.
 */
const BRAND_HEX = '#15526e';
const BRAND_RGB = '21, 82, 109';
export const MAIN_COLOR = `rgb(${BRAND_RGB})`;
export const FILL_COLOR = `${BRAND_HEX}14`;                    // brand at ~8% alpha (8-digit hex)
/**
 * The goal threshold line — and on the metric screen's line, area, scatter and lollipop
 * charts it is the ONLY on-canvas rendering of the goal, so it carries WCAG 1.4.11's 3:1
 * non-text floor. The previous rgba(156,163,175,0.6) composited to #C4C8CF, 1.68:1 on the
 * white card: legible as a hint, invisible as a threshold. Solid #6B7280 is 4.83:1 and, being
 * opaque, matches the legend swatch exactly instead of approximately. The [8,4] dash keeps it
 * distinct from a data line, so weight is not the only thing telling them apart. The scorecard
 * drawer hands it to the Superset bundle's styling hook for its goal series, with the same dash
 * in the goal's series style.
 */
export const GOAL_LINE_COLOR = '#6B7280';                      // matches the #6b7280 legend swatch
export const AXIS_POINTER_COLOR = `rgba(${BRAND_RGB}, 0.25)`;

/**
 * The scorecard drawer's goal-attainment fill bands, drawn under its sparkline.
 *
 * The drawer's contract names the side of the goal that is good (a series style's
 * goalBand.better, chart-contract.md), and the Superset bundle's styling hook paints the bands in
 * these tones, which the drawer hands it as the host's `styleColors.goalBand`. Kept here, not in
 * the contract, so the tones have one home.
 *
 * Named by VALENCE, not by position: the good tone goes to the band BELOW the goal whenever
 * lower-is-better, so an above/below name is wrong exactly half the time and invites the next
 * caller to pick by position and invert the bands.
 */
export const GOAL_BAND_GOOD_COLOR = 'rgba(134, 239, 172, 0.2)';
export const GOAL_BAND_BAD_COLOR = 'rgba(252, 165, 165, 0.2)';

/**
 * Brand ink at an explicit alpha.
 *
 * A helper rather than `MAIN_COLOR.replace('rgb', 'rgba')` at each call site: three such
 * rewrites had crept in, and one of them recovered a colour by string-replacing the alpha
 * out of a band constant — which silently produces the wrong colour, with no error, the
 * moment that band's opacity is retuned here.
 */
export const brandAlpha = (alpha) => `rgba(${BRAND_RGB}, ${alpha})`;

/**
 * A mark the reader is meant to notice as underperforming — a bar that missed its goal, and
 * a lollipop's stem — plus a waterfall's decrease bar.
 *
 * Named by valence for the same reason as the bands above: barItemColor() picks this by
 * whether the point MISSED its goal, which for a lower-is-better metric is the bar ABOVE the
 * goal line.
 *
 * Both are solid marks a reader needs to distinguish from the chart's white card, so they
 * carry WCAG 1.4.11's 3:1 non-text-contrast floor. The earlier values did not: brand ink at
 * 45% alpha composites to #96B1BD (2.25:1), and the goal band's own 20%-alpha red pushed to
 * 85% composites to #FCB2B2 (1.73:1) — the least legible mark on a waterfall was its most
 * important one. These clear the floor: brandAlpha(0.62) composites to #6E94A4 at 3.26:1,
 * and #E02424 is 4.72:1 on white.
 */
export const BAR_MISSED_GOAL_COLOR = brandAlpha(0.62);
export const WATERFALL_DECREASE_COLOR = '#E02424';

/**
 * The categorical ramp the site analytics report inks its lines with — one colour per
 * ReportDefinition::colorIndex() slot, so Sessions keeps its colour on the trend and inside an
 * expanded Explore row whichever payload carried it. The donut draws in the workspace's
 * dashboard palette and the bars in one brand colour, so neither takes a slot from here.
 *
 * Distinct HUES, not one hue stepped in opacity like SLICE_ALPHAS above. That ramp inks
 * slices of ONE quantity, where a shared hue is the point; these are different quantities on
 * one canvas (sessions against clicks), and an opacity ramp would say they are the same
 * measure at different weights. The first five slots are TREND_SERIES' own order, so the
 * series a reader sees together on first load are the five most separated hues.
 *
 * Every entry is a solid mark carrying meaning against the white card, so each owes WCAG
 * 1.4.11's 3:1 non-text floor. Measured on #ffffff, in order:
 *   #15526E 8.52:1   #C2410C 5.18:1   #047857 5.48:1   #7C3AED 5.70:1
 *   #B91C1C 6.47:1   #0891B2 3.68:1   #A16207 4.92:1   #BE185D 6.04:1
 * Colour is never the only channel: every series is named in the legend, in its toggle pill
 * and in the visually-hidden series table, and the tooltip prints the series name beside its
 * value (WCAG 1.4.1).
 *
 * Literal hex strings, and eight of them: colorIndex() returns a stable slot that can run
 * past the trend's five (a measure no trend series offers still gets one), so callers index
 * this modulo its length and two distant slots may share a colour — the legend's labels, the
 * tooltip's rows and the series table are what tell them apart when they do. No card draws two
 * such lines together today: each card's contract gives its lines distinct slots.
 */
export const CATEGORICAL_COLORS = Object.freeze([
    '#15526E',
    '#C2410C',
    '#047857',
    '#7C3AED',
    '#B91C1C',
    '#0891B2',
    '#A16207',
    '#BE185D',
]);

/**
 * The Stats topic chart's bands in the v3 design (D63, D67): the eight a default stack draws, plus
 * Everything else, the one band beside chosen topics. Keyed as TopicFigures and TopicsCardBuilder key
 * their bands; `topics` holds the colours of the top three topics by clicks, in rank order.
 *
 * The values are the spec's. Everything else (#cfc6b9) is 1.69:1 on white, under WCAG 1.4.11's 3:1
 * for a chart mark: the task that draws the bands (S47) checks each against it, not this list.
 */
export const TOPIC_BAND_COLORS = Object.freeze({
    topics: Object.freeze(['#1d4e63', '#c2410c', '#0f766e']),
    other_topics: '#b45309',
    branded_queries: '#a21caf',
    not_relevant: '#78716c',
    other_queries: '#65a30d',
    hidden_by_google: '#be185d',
    everything_else: '#cfc6b9',
});

/**
 * The colours a topic drawn by choice rather than by rank takes, in the order new topics take them
 * (D67). A topic keeps its colour everywhere it is drawn: its band, its legend entry and its table dot.
 */
export const NEW_TOPIC_COLORS = Object.freeze([
    '#0e7490',
    '#9333ea',
    '#ca8a04',
    '#15803d',
    '#db2777',
    '#4f46e5',
]);

/** The colour of a payload series, by its server-assigned ReportDefinition::colorIndex(). */
export const categoricalColor = (colorIndex) =>
    CATEGORICAL_COLORS[((Math.trunc(colorIndex) % CATEGORICAL_COLORS.length) + CATEGORICAL_COLORS.length) % CATEGORICAL_COLORS.length];
